// CONCEPT: data-whlc / data-whsn on an order-modal row are warehouse identifiers for the
// part the row ARRIVED as, not for the part it currently names.
// loadQuickOrderModalLinesFromOrder() stamps them once, from the stored order line, and
// nothing else in src/js writes them - so from the moment the buyer retypes that row's part
// number or brand they describe a different part than the row does.
//
// That mismatch is not cosmetic, because the stale half wins. getMultiItemInventory() reads
// the part number from the LIVE input but the identifiers from those attributes, and
// Whs::createPiesItems() prefers `stocknumber` over `partnumber` when it derives
// wh_stocknumber. So an edited reorder row was stocked and priced as the part it used to be,
// displayed under the number the buyer just typed, and still submittable on that basis
// (#700). The per-row path has the same shape: getItemInventory() falls back to these
// attributes for any identifier its caller left empty.
//
// Dropping them puts the row back on the derivation createPiesItems() does for a row nobody
// reordered - wh_linecode from get_brand($aaia)->WarehouseBrandCode, wh_stocknumber from the
// part number. That is what a row the buyer typed should be using. The fix is deliberately
// NOT to reorder that server-side preference: for an UNEDITED reorder line the stored
// WHStockNumber is the more precise identifier of the two, and the reorder path depends on it.
//
// Which is why this remembers rather than just clears. Dropping the stamps on the first
// keystroke would make the loss permanent, and typo-then-undo is an ordinary interaction: a
// buyer who mistypes a part number and immediately corrects it would be left with a row that
// names the original line but resolves by the less precise path. So the identity the stamps
// belong to is recorded alongside them, and they come back the moment the inputs match it
// again. While the inputs differ, they stay off.
//
// USE: rememberReorderedRowIdentity($row) once, where a row is stamped;
// syncRowWarehouseIdentifiers($row) whenever its part number or brand changes;
// rowPartIdentity($row) to ask "which item does this row name?" - exported because the
// rendered inventory has to answer the same question against the same normalisation
// (ordering/inventory.js), and two spellings of it would drift the way #301's four
// stock-display copies did.

var IDENTITY_KEY = 'paceReorderIdentity';

/**
 * The comparison has to answer the same question the request does - "is this still the same
 * item?" - so it normalises the way the lookup behind the request does. Every site table
 * these part numbers are matched against collates *_ci (utf8mb4_general_ci, utf8mb3_general_ci,
 * latin1_swedish_ci), so PN-1 and pn-1 resolve to one row and treating a case change as an
 * edit would give up precision for nothing. partials/product-browse.js builds its own
 * row-to-response correlation token the same way. Trimming matches the batch builder, which
 * decides whether a row is worth posting on String(partNumber).trim().
 */
function normalise(value) {
    return String(value == null ? '' : value)
        .trim()
        .toUpperCase();
}

/**
 * Which item the row names right now, normalised. Both fields, because a row is identified
 * by the pair: createPiesItems() derives wh_linecode from the brand, so the same part number
 * under a different brand is a different lookup.
 */
export function rowPartIdentity($row) {
    return {
        partNumber: normalise($row.find('.multi-order-part-number').val()),
        aaia: normalise($row.find('.multi-order-brand').val()),
    };
}

/**
 * Record which part the row's stamps belong to. Held in jQuery's per-element data store
 * rather than in an attribute on purpose: .data() is keyed on the element itself, is never
 * serialised into the form, and - unlike an attribute - is NOT carried by the plain .clone()
 * every row-creation path in the modal uses, so a blank .default-row cloned off a table that
 * contains reordered rows cannot inherit an identity it never had.
 */
export function rememberReorderedRowIdentity($row) {
    var lineCode = $row.attr('data-whlc');
    var stockNumber = $row.attr('data-whsn');

    if (!lineCode && !stockNumber) {
        return;
    }

    var identity = rowPartIdentity($row);

    // A row with no part number yet is not an identity anything can be restored to - it would
    // mean "put the stamps back whenever this row is emptied", which is the opposite of the
    // point. Call this after the row's inputs are populated.
    if (!identity.partNumber.length) {
        return;
    }

    identity.lineCode = lineCode;
    identity.stockNumber = stockNumber;

    $row.data(IDENTITY_KEY, identity);
}

/**
 * Drop the stamps while the row names something other than what they describe, and put them
 * back when it names it again. A row with stamps but no remembered identity is cleared rather
 * than trusted: whatever stamped it did not say what it meant, and #700 is what happens when
 * an unexplained identifier is allowed to outlive the row it described.
 */
export function syncRowWarehouseIdentifiers($row) {
    var original = $row.data(IDENTITY_KEY);
    var current = rowPartIdentity($row);

    if (
        original &&
        current.partNumber === original.partNumber &&
        current.aaia === original.aaia
    ) {
        if (original.lineCode) {
            $row.attr('data-whlc', original.lineCode);
        }
        if (original.stockNumber) {
            $row.attr('data-whsn', original.stockNumber);
        }

        return;
    }

    $row.removeAttr('data-whlc').removeAttr('data-whsn');
}
